import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { useSelector, useMakeSelector } from 'hooks/redux'
import strings from 'utils/strings'
import { LIST, DASH_VIEW } from 'utils/constants'
import {
  setSelectedIdWithMeta,
  setSelectedIdWithDrawerAndMeta,
} from 'actions/navigator'
import {
  saveApprovedLayout,
  setHasApprovedLayout,
  SUCCESS_SAVE_APPROVED_LAYOUT,
} from 'actions/dash'
import {
  Button,
  Paragraph,
  Header,
  FlexRow,
  FlexCol,
} from 'components/patterns'
import { TextArea } from 'components/deprecated/Inputs'
import { Content, Footer } from 'components/SwitchAndGauge/DashDrawer/Sections'
import styled from '@emotion/styled'
import { getDashVehicleSerialNumber } from 'selectors/dash'
import { makeGetIsWaiting } from 'selectors/application'
import { capitalizeFirstOnly } from 'utils/utils'
import { StyledViewWrap } from './style'

const SaveLayout = () => {
  const dispatch = useDispatch()

  const [notes, setNotes] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const serialNumber = useSelector(getDashVehicleSerialNumber)

  const isLoading = useMakeSelector(makeGetIsWaiting, {
    signals: [`${SUCCESS_SAVE_APPROVED_LAYOUT}${serialNumber}`],
  })

  const handleAfterSave = useCallback(() => {
    dispatch(setHasApprovedLayout(true))
    dispatch(
      setSelectedIdWithMeta(DASH_VIEW, {
        view: LIST,
      })
    )
    setIsSaving(false)
  }, [dispatch])

  const handleOnCancel = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LIST,
      })
    )
  }

  const handleOnSave = () => {
    setIsSaving(true)
    dispatch(
      saveApprovedLayout({
        serialNumber,
        body: {
          notes,
        },
      })
    )
  }

  const handleOnChangeNotes = (e) => {
    setNotes(e.currentTarget.value)
  }

  useEffect(() => {
    if (isLoading === false && isSaving) handleAfterSave()
  }, [isLoading, isSaving, handleAfterSave])

  const disableSaveBtn = notes.length === 0 || isLoading

  return (
    <StyledViewWrap>
      <Content>
        <FlexRow>
          <FlexCol span={24}>
            <HeaderSection>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={20}
                height={18}
                fill="none"
                style={{ marginRight: 8 }}
              >
                <path
                  fill="#EFB115"
                  fillRule="evenodd"
                  d="M19.71 14.82 11.84 1.06a2.09 2.09 0 0 0-3.63 0L.31 14.82A2.12 2.12 0 0 0 2.1 18h15.8a2.12 2.12 0 0 0 1.81-3.18zM11 14.5H9v-2h2v2zm0-5a1 1 0 0 1-2 0v-2a1 1 0 0 1 2 0v2z"
                  clipRule="evenodd"
                />
                <path
                  fill="#07070F"
                  d="M11 12.5H9v2h2v-2zm0-3v-2a1 1 0 0 0-2 0v2a1 1 0 0 0 2 0z"
                />
              </svg>
              <Header type="h2">{strings.editing_switch_and_gauge}</Header>
            </HeaderSection>
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol span={24}>
            <Paragraph bold size="small" style={{ padding: '0 4px' }}>
              {strings.updated_the_following}
            </Paragraph>
            <ul>
              <li>
                <Paragraph>
                  {capitalizeFirstOnly(strings.dash_layout)}
                </Paragraph>
              </li>
            </ul>
          </FlexCol>
        </FlexRow>
        <FlexRow>
          <FlexCol span={24}>
            <TextArea
              size="large"
              onChange={handleOnChangeNotes}
              value={notes}
              placeholder={strings.notes_required}
              autosize={{ minRows: 8, maxRows: 15 }}
              maxLength={1000}
              required
              label={strings.notes}
            />
            <br />
          </FlexCol>
        </FlexRow>
      </Content>

      <Footer>
        <FlexRow>
          <FlexCol span={12}>
            <div>
              <Button onClick={handleOnCancel} type="secondary">
                {strings.cancel}
              </Button>
            </div>
          </FlexCol>
          <FlexCol span={12} alignItems="end">
            <div>
              <Button
                onClick={handleOnSave}
                type="primary"
                disabled={disableSaveBtn}
                loading={isLoading}
              >
                {isLoading ? strings.saving : strings.save}
              </Button>
            </div>
          </FlexCol>
        </FlexRow>
      </Footer>
    </StyledViewWrap>
  )
}

export default SaveLayout

SaveLayout.propTypes = {
  id: PropTypes.string, // location name
}

const HeaderSection = styled.div`
  display: flex;
  flex-direction: row;
  margin-bottom: 16px;
  align-items: center;
`
